import React, { forwardRef, useState } from 'react';

interface SelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label?: string;
  options: SelectOption[];
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  onChange?: (value: string) => void;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ 
    label, 
    options, 
    error, 
    helperText, 
    fullWidth = false, 
    className = '', 
    onChange,
    onFocus,
    onBlur,
    ...props 
  }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    
    const baseSelectClasses = `
      w-full px-4 py-3 rounded-xl appearance-none cursor-pointer
      bg-gray-900/50 backdrop-blur-md
      border transition-all duration-300
      text-white
      focus:outline-none
    `;
    
    const stateClasses = error 
      ? 'border-red-500/50 focus:border-red-400 focus:ring-2 focus:ring-red-500/20' 
      : `border-gray-700/50 hover:border-gray-600/70 
         focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/20
         focus:shadow-lg focus:shadow-primary-500/10`;
    
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (onChange) {
        onChange(e.target.value);
      }
    };
    
    const handleFocus = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };
    
    const handleBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };
    
    const selectedOption = options.find(opt => opt.value === props.value);
    
    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label 
            className={`
              block text-sm font-medium mb-2 transition-colors duration-300
              ${isFocused ? 'text-primary-400' : 'text-gray-300'}
              ${error ? 'text-red-400' : ''}
            `}
          >
            {label}
          </label>
        )}
        
        <div className="relative group">
          {/* Glow effect on focus */}
          <div 
            className={`
              absolute -inset-0.5 rounded-xl opacity-0 blur-sm transition-opacity duration-300
              ${isFocused && !error ? 'opacity-50' : ''}
            `}
            style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 242, 0.3), rgba(0, 31, 89, 0.5))'
            }}
          />
          
          {/* Selected option icon */}
          {selectedOption?.icon && (
            <div 
              className={`
                absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none z-10
                transition-colors duration-300
                ${isFocused ? 'text-primary-400' : 'text-gray-500'}
              `}
            >
              {selectedOption.icon}
            </div>
          )}
          
          <select
            ref={ref}
            className={`
              ${baseSelectClasses}
              ${stateClasses}
              ${selectedOption?.icon ? 'pl-12' : ''}
              ${className}
              relative
            `.replace(/\s+/g, ' ').trim()}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            {...props}
          >
            {options.map((option) => (
              <option 
                key={option.value} 
                value={option.value}
                disabled={option.disabled}
                className="bg-gray-900 text-white"
              >
                {option.label}
              </option>
            ))}
          </select>
          
          {/* Custom dropdown arrow */}
          <div 
            className={`
              absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none
              transition-all duration-300
              ${isFocused ? 'text-primary-400' : 'text-gray-500'}
            `}
          >
            <svg 
              className={`h-4 w-4 transition-transform duration-300 ${isFocused ? 'rotate-180' : ''}`} 
              xmlns="http://www.w3.org/2000/svg" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
                clipRule="evenodd" 
              />
            </svg>
          </div>
        </div>
        
        {(error || helperText) && (
          <p 
            className={`
              mt-2 text-sm transition-colors duration-300
              ${error ? 'text-red-400' : 'text-gray-500'}
            `}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
